/** The editor's unsaved copy of an estimate and every edit the screen can make to it. Pure. */
import { round2, type BudgetInput, type BudgetOption, type EquipmentInput, type EstimateInputs } from '@atek/estimate-pricing';
import type { ContextBlock, Estimate, Lang, Presenter, SaveEstimateBody } from '../types';

export const MAX_BUDGETS = 8;
export const MAX_CONTEXT_BLOCKS = 4;

export interface EditorDraft {
  title: string;
  language: Lang;
  validUntil: string;
  siteAddress: string;
  coverContactId: string | null;
  presenter: Presenter;
  businessContext: ContextBlock[];
  inputs: EstimateInputs;
}

export type EditorAction =
  | { type: 'reset'; draft: EditorDraft }
  | { type: 'setField'; field: 'title' | 'language' | 'validUntil' | 'siteAddress' | 'coverContactId'; value: string | null }
  | { type: 'setPresenter'; field: keyof Presenter; value: string }
  | { type: 'setInputs'; patch: Partial<EstimateInputs> }
  | { type: 'setConnectivity'; mode: 'wifi' | 'gateway' }
  | { type: 'setEquipment'; index: number; patch: Partial<EquipmentInput> }
  | { type: 'addEquipment'; typeKey: string }
  | { type: 'removeEquipment'; index: number }
  | { type: 'setBudget'; index: number; patch: Partial<BudgetInput> }
  | { type: 'setBudgetOption'; index: number; option: BudgetOption; value: boolean }
  | { type: 'addBudget' }
  | { type: 'duplicateBudget'; index: number }
  | { type: 'removeBudget'; index: number }
  | { type: 'setContext'; index: number; patch: Partial<ContextBlock> }
  | { type: 'addContext' }
  | { type: 'removeContext'; index: number };

export function draftFromEstimate(e: Estimate): EditorDraft {
  return {
    title: e.title,
    language: e.language,
    validUntil: e.validUntil,
    siteAddress: e.siteAddress ?? '',
    coverContactId: e.coverContactId ?? null,
    presenter: { name: e.presenter?.name ?? '', title: e.presenter?.title ?? '', phone: e.presenter?.phone ?? '', email: e.presenter?.email ?? '' },
    businessContext: (e.businessContext ?? []).map((b) => ({ heading: b.heading, body: b.body })),
    inputs: structuredClone(e.inputs),
  };
}

export function nextBudgetKey(budgets: BudgetInput[]): string {
  const used = new Set(budgets.map((b) => b.key));
  let n = 1;
  while (used.has(`b${n}`)) n += 1;
  return `b${n}`;
}

/** The discount a typed unit price means against the list price; null when it is at or above list. */
export function priceToDiscount(price: number, list: number): number | null {
  if (!list) return null;
  const pct = round2((1 - price / list) * 100);
  return pct <= 0 ? null : Math.min(100, pct);
}

const withInputs = (state: EditorDraft, patch: Partial<EstimateInputs>): EditorDraft => ({ ...state, inputs: { ...state.inputs, ...patch } });
const replaceAt = <T,>(list: T[], index: number, value: T) => list.map((item, i) => (i === index ? value : item));

export function editorReducer(state: EditorDraft, action: EditorAction): EditorDraft {
  const { inputs } = state;
  switch (action.type) {
    case 'reset':
      return action.draft;
    case 'setField':
      return { ...state, [action.field]: action.value };
    case 'setPresenter':
      return { ...state, presenter: { ...state.presenter, [action.field]: action.value } };
    case 'setInputs':
      return withInputs(state, action.patch);
    case 'setConnectivity':
      return withInputs(state, { connectivity: action.mode === 'wifi' ? { mode: 'wifi' } : { mode: 'gateway', gateways: 1, dataPlans: 1 } });
    case 'setEquipment':
      return withInputs(state, { equipment: replaceAt(inputs.equipment, action.index, { ...inputs.equipment[action.index], ...action.patch }) });
    case 'addEquipment':
      if (inputs.equipment.some((row) => row.typeKey === action.typeKey)) return state;
      return withInputs(state, { equipment: [...inputs.equipment, { typeKey: action.typeKey, qty: 1, calibrated: false }] });
    case 'removeEquipment':
      return withInputs(state, { equipment: inputs.equipment.filter((_, i) => i !== action.index) });
    case 'setBudget':
      return withInputs(state, { budgets: replaceAt(inputs.budgets, action.index, { ...inputs.budgets[action.index], ...action.patch }) });
    case 'setBudgetOption': {
      const budget = inputs.budgets[action.index];
      return withInputs(state, { budgets: replaceAt(inputs.budgets, action.index, { ...budget, options: { ...budget.options, [action.option]: action.value } }) });
    }
    case 'addBudget': {
      if (inputs.budgets.length >= MAX_BUDGETS) return state;
      const last = inputs.budgets[inputs.budgets.length - 1];
      const budget: BudgetInput = {
        key: nextBudgetKey(inputs.budgets),
        label: `Budget ${inputs.budgets.length + 1}`,
        subtitle: null,
        options: last ? { ...last.options } : { installation: true, configuration: true, calibration: false, iqoq: false },
        discountPct: 0,
      };
      return withInputs(state, { budgets: [...inputs.budgets, budget] });
    }
    case 'duplicateBudget': {
      if (inputs.budgets.length >= MAX_BUDGETS) return state;
      const source = inputs.budgets[action.index];
      // The label goes into the deck and the quote, so the suffix follows the deck's language.
      const suffix = state.language === 'fr' ? 'copie' : 'copy';
      const copy: BudgetInput = { ...source, key: nextBudgetKey(inputs.budgets), label: `${source.label} (${suffix})`.slice(0, 80), options: { ...source.options } };
      const budgets = [...inputs.budgets];
      budgets.splice(action.index + 1, 0, copy);
      return withInputs(state, { budgets });
    }
    case 'removeBudget':
      if (inputs.budgets.length <= 1) return state;
      return withInputs(state, { budgets: inputs.budgets.filter((_, i) => i !== action.index) });
    case 'setContext':
      return { ...state, businessContext: replaceAt(state.businessContext, action.index, { ...state.businessContext[action.index], ...action.patch }) };
    case 'addContext':
      if (state.businessContext.length >= MAX_CONTEXT_BLOCKS) return state;
      return { ...state, businessContext: [...state.businessContext, { heading: '', body: '' }] };
    case 'removeContext':
      return { ...state, businessContext: state.businessContext.filter((_, i) => i !== action.index) };
    default:
      return state;
  }
}

/** Key-order independent, and an empty value (null/undefined) counts as absent. */
function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value as Record<string, unknown>).sort()
        .filter((key) => (value as Record<string, unknown>)[key] !== null && (value as Record<string, unknown>)[key] !== undefined)
        .map((key) => [key, stable((value as Record<string, unknown>)[key])]),
    );
  }
  return value;
}

export function isDirty(draft: EditorDraft, estimate: Estimate): boolean {
  return JSON.stringify(stable(draft)) !== JSON.stringify(stable(draftFromEstimate(estimate)));
}

export function toSaveBody(draft: EditorDraft, expectedUpdatedAt: string): SaveEstimateBody {
  return {
    expectedUpdatedAt,
    title: draft.title.trim(),
    language: draft.language,
    validUntil: draft.validUntil,
    siteAddress: draft.siteAddress.trim() || null,
    coverContactId: draft.coverContactId,
    presenter: draft.presenter,
    // Blank blocks are dropped; a block with text but no heading is sent so the server names the missing heading.
    businessContext: draft.businessContext
      .filter((b) => b.heading.trim() || b.body.trim())
      .map((b) => ({ heading: b.heading.trim(), body: b.body.trim() })),
    inputs: draft.inputs,
  };
}
